import { useRef } from "react";

import { cn } from "@/lib/utils";

/**
 * Six segmented boxes for a one-time code.
 *
 * Replaces a single letter-spaced text field. Segmented entry is what every
 * reference does — Braintrust, Otter, Vercel, Remote, Uber, Cal.com, Aboard,
 * Bonsai — and it is not only convention: a field of six boxes shows how many
 * digits are expected and how many remain without any helper text.
 *
 * Used by the login challenge, the enrollment confirm step and the step-up
 * dialog, so the three 2FA moments look like one feature.
 */
export function CodeInput({
  value,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
  length = 6,
  id,
  className,
}: {
  /** The digits so far, as one string. Never longer than `length`. */
  value: string;
  onChange: (next: string) => void;
  /** Fired once the last box is filled — lets a caller submit without a click. */
  onComplete?: (code: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  length?: number;
  id?: string;
  /** The row's own classes — a left-aligned form overrides the centring. */
  className?: string;
}): JSX.Element {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.padEnd(length, " ").slice(0, length).split("");

  function commit(next: string): void {
    const clean = next.replace(/\D/g, "").slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
  }

  function setAt(index: number, raw: string): void {
    const clean = raw.replace(/\D/g, "");
    if (clean === "") return;

    // A PASTE lands in one box but belongs across all of them. Without this,
    // pasting a code from a password manager fills a single digit and drops
    // the rest, which is the most common way these inputs disappoint.
    if (clean.length > 1) {
      const merged = (value.slice(0, index) + clean).slice(0, length);
      commit(merged);
      refs.current[Math.min(merged.length, length - 1)]?.focus();
      return;
    }

    const next = (value.slice(0, index) + clean + value.slice(index + 1)).slice(0, length);
    commit(next);
    refs.current[Math.min(index + 1, length - 1)]?.focus();
  }

  return (
    <div className={cn("flex justify-center gap-2", className)} role="group" id={id}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          inputMode="numeric"
          /* Only the first box advertises one-time-code: marking every box
             makes some password managers offer to fill each one separately. */
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`${i + 1}`}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          maxLength={length}
          value={digit.trim()}
          onChange={(ev) => setAt(i, ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === "Backspace") {
              // Backspace on an EMPTY box steps back and clears the previous
              // one; otherwise correcting a typo means reaching for the mouse.
              if (digit.trim() === "" && i > 0) {
                ev.preventDefault();
                commit(value.slice(0, i - 1));
                refs.current[i - 1]?.focus();
              }
              return;
            }
            if (ev.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
            if (ev.key === "ArrowRight" && i < length - 1) refs.current[i + 1]?.focus();
          }}
          className={cn(
            "h-14 w-12 rounded-lg border bg-background text-center text-2xl font-medium text-foreground outline-none transition-colors",
            "focus:border-primary focus:ring-2 focus:ring-primary/20",
            disabled && "opacity-60"
          )}
        />
      ))}
    </div>
  );
}
